/**
 * The live token counter and the per-chat session total: parsing of the server's figures (never trusted blindly),
 * k/M formatting, and the arithmetic that adds finished turns up per conversation. Pure functions and one small
 * store, no React. Everything here is client-side state: nothing new is sent to or kept on the server.
 */
export const USAGE_PHASES = ['thinking', 'responding', 'tool'];
export const EMPTY_SESSION_USAGE = Object.freeze({ inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0, costUsd: 0, turns: 0 });
function count(value) {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER ? value : undefined;
}
/** An optional count: absent reads as 0, present must be a non-negative integer. */
function optionalCount(value) {
    return value === undefined || value === null ? 0 : count(value);
}
function money(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}
function record(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
}
/** The optional contextTokens figure: absent stays absent (undefined, never defaulted to 0), malformed is null. */
function contextFigure(value) {
    if (value === undefined || value === null)
        return undefined;
    return count(value) ?? null;
}
/** The conversation size to carry: the newer figure when there is one, else the older one, else none. */
function latestContext(newer, older) {
    const contextTokens = newer ?? older;
    return contextTokens === undefined ? {} : { contextTokens };
}
/** A `usage` stream event, or null when any figure is malformed (nothing is repaired or defaulted). */
export function parseLiveUsage(data) {
    const d = record(data);
    if (!d)
        return null;
    const inputTokens = count(d.inputTokens), outputTokens = count(d.outputTokens), round = count(d.round);
    if (inputTokens === undefined || outputTokens === undefined || round === undefined || round < 1)
        return null;
    if (!USAGE_PHASES.includes(d.phase))
        return null;
    const cacheReadTokens = optionalCount(d.cacheReadTokens), cacheCreationTokens = optionalCount(d.cacheCreationTokens), thinkingTokens = optionalCount(d.thinkingTokens);
    if (cacheReadTokens === undefined || cacheCreationTokens === undefined || thinkingTokens === undefined)
        return null;
    const hasCost = d.costUsd !== undefined && d.costUsd !== null;
    const costUsd = hasCost ? money(d.costUsd) : undefined;
    if (hasCost && costUsd === undefined)
        return null;
    const contextTokens = contextFigure(d.contextTokens);
    if (contextTokens === null)
        return null;
    return { inputTokens, outputTokens, cacheReadTokens, cacheCreationTokens, thinkingTokens, ...(costUsd !== undefined ? { costUsd } : {}), estimated: d.estimated === true, round, phase: d.phase, ...latestContext(contextTokens, undefined) };
}
/** The `usage` block of a done result, or null when absent or malformed. */
export function parseTurnUsage(data) {
    const d = record(data);
    if (!d)
        return null;
    const inputTokens = count(d.inputTokens), outputTokens = count(d.outputTokens);
    const cacheReadTokens = optionalCount(d.cacheReadTokens), cacheWriteTokens = optionalCount(d.cacheWriteTokens), webSearchRequests = optionalCount(d.webSearchRequests);
    const costUsd = d.costUsd === undefined || d.costUsd === null ? 0 : money(d.costUsd);
    const contextTokens = contextFigure(d.contextTokens);
    if ([inputTokens, outputTokens, cacheReadTokens, cacheWriteTokens, webSearchRequests, costUsd].some((v) => v === undefined) || contextTokens === null)
        return null;
    return { inputTokens: inputTokens, outputTokens: outputTokens, cacheReadTokens: cacheReadTokens, cacheWriteTokens: cacheWriteTokens, webSearchRequests: webSearchRequests, costUsd: costUsd, ...latestContext(contextTokens, undefined) };
}
/** A session total read back from the local chat cache, or null when it is not one. */
export function parseSessionUsage(data) {
    const d = record(data);
    if (!d)
        return null;
    const inputTokens = count(d.inputTokens), outputTokens = count(d.outputTokens), cacheReadTokens = count(d.cacheReadTokens), cacheWriteTokens = count(d.cacheWriteTokens), turns = count(d.turns);
    const costUsd = money(d.costUsd);
    const contextTokens = contextFigure(d.contextTokens);
    if ([inputTokens, outputTokens, cacheReadTokens, cacheWriteTokens, turns, costUsd].some((v) => v === undefined) || contextTokens === null)
        return null;
    return { inputTokens: inputTokens, outputTokens: outputTokens, cacheReadTokens: cacheReadTokens, cacheWriteTokens: cacheWriteTokens, costUsd: costUsd, turns: turns, ...latestContext(contextTokens, undefined) };
}
/** A finished turn added to a chat's total. */
export function addTurnUsage(total, turn) {
    return {
        inputTokens: total.inputTokens + turn.inputTokens,
        outputTokens: total.outputTokens + turn.outputTokens,
        cacheReadTokens: total.cacheReadTokens + turn.cacheReadTokens,
        cacheWriteTokens: total.cacheWriteTokens + turn.cacheWriteTokens,
        costUsd: total.costUsd + turn.costUsd,
        turns: total.turns + 1,
        // The chat's size is the latest turn's, never a sum.
        ...latestContext(turn.contextTokens, total.contextTokens),
    };
}
/** What a turn spent when it ended without a recorded usage (stopped, errored, or an older server): its last live figures. */
export function turnUsageFromLive(live) {
    return { inputTokens: live.inputTokens, outputTokens: live.outputTokens, cacheReadTokens: live.cacheReadTokens, cacheWriteTokens: live.cacheCreationTokens, webSearchRequests: 0, costUsd: live.costUsd ?? 0, ...latestContext(live.contextTokens, undefined) };
}
/** The larger of two figures for one turn, field by field. */
function largerTurnUsage(a, b) {
    return {
        inputTokens: Math.max(a.inputTokens, b.inputTokens),
        outputTokens: Math.max(a.outputTokens, b.outputTokens),
        cacheReadTokens: Math.max(a.cacheReadTokens, b.cacheReadTokens),
        cacheWriteTokens: Math.max(a.cacheWriteTokens, b.cacheWriteTokens),
        webSearchRequests: Math.max(a.webSearchRequests, b.webSearchRequests),
        costUsd: Math.max(a.costUsd, b.costUsd),
        // The later call read the longer prompt: the larger size is the conversation as it last stood.
        ...latestContext(a.contextTokens !== undefined && b.contextTokens !== undefined ? Math.max(a.contextTokens, b.contextTokens) : a.contextTokens, b.contextTokens),
    };
}
/**
 * What a turn that reached its end adds to its chat, and whether that is final (`recorded`: nothing replaces it).
 * A turn that completed adds its done usage, which is what the service charged and what the live counter ended on.
 * A turn that failed, or whose done usage reports nothing, adds the larger of the done usage and its last live
 * figures, field by field: the done usage leaves out a model call that failed part-way, and an agent task that timed
 * out reports none at all, yet the counter showed those tokens being spent, so the chat's total never drops below
 * what the person saw. Without a done usage (an older server) the last live figures count provisionally.
 */
export function finishedTurnUsage(recorded, live, failed) {
    const shown = live ? turnUsageFromLive(live) : undefined;
    if (!recorded)
        return shown ? { usage: shown, recorded: false } : null;
    if (!shown || (!failed && totalTokens(recorded) > 0))
        return { usage: recorded, recorded: true };
    return { usage: largerTurnUsage(recorded, shown), recorded: true };
}
/** The chat's total with the turn in flight on top (not counted as a turn until it ends). */
export function sessionUsageWithLive(total, live) {
    if (!live)
        return total;
    return {
        inputTokens: total.inputTokens + live.inputTokens,
        outputTokens: total.outputTokens + live.outputTokens,
        cacheReadTokens: total.cacheReadTokens + live.cacheReadTokens,
        cacheWriteTokens: total.cacheWriteTokens + live.cacheCreationTokens,
        costUsd: total.costUsd + (live.costUsd ?? 0),
        turns: total.turns,
        // The turn in flight is reading the chat as it now stands.
        ...latestContext(live.contextTokens, total.contextTokens),
    };
}
/** Every token the service charges for: input, output and both kinds of cache token. */
export function totalTokens(usage) {
    return usage.inputTokens + usage.outputTokens + usage.cacheReadTokens + usage.cacheWriteTokens;
}
/**
 * Every token the turn in flight has used so far, counted the way the session total and the per-person caps count
 * (input, output and both kinds of cache token). The live counter's headline: it is never stuck at zero while a
 * model thinks (the prompt is spent the moment a call starts). A turn that completes adds exactly its last figure to
 * the chat (the counter ends on the done usage); a failed turn adds at least what the counter showed.
 */
export function liveTotalTokens(live) {
    return live.inputTokens + live.outputTokens + live.cacheReadTokens + live.cacheCreationTokens;
}
/** A turn's figures taken back out of a total (a provisional figure being replaced); never below zero, turns unchanged. */
function withoutTurnUsage(total, turn) {
    return {
        inputTokens: Math.max(0, total.inputTokens - turn.inputTokens),
        outputTokens: Math.max(0, total.outputTokens - turn.outputTokens),
        cacheReadTokens: Math.max(0, total.cacheReadTokens - turn.cacheReadTokens),
        cacheWriteTokens: Math.max(0, total.cacheWriteTokens - turn.cacheWriteTokens),
        costUsd: Math.max(0, total.costUsd - turn.costUsd),
        turns: total.turns,
        // The size stays until the replacing turn brings its own (addTurnUsage).
        ...latestContext(total.contextTokens, undefined),
    };
}
/** How many turns' contributions the per-chat store remembers (enough to settle any turn still in flight). */
const COUNTED_TURNS_KEPT = 200;
function sameSessionUsage(a, b) {
    return a !== undefined && a.inputTokens === b.inputTokens && a.outputTokens === b.outputTokens && a.cacheReadTokens === b.cacheReadTokens
        && a.cacheWriteTokens === b.cacheWriteTokens && a.costUsd === b.costUsd && a.turns === b.turns && a.contextTokens === b.contextTokens;
}
/**
 * The chat list with one chat's total replaced, for the local chat cache. A total is written where it changes (a turn
 * counted, a failed turn, a reply cut off by a new chat), not only when the transcript is saved: a failed or abandoned
 * turn leaves the transcript as it was, yet what it spent belongs to the chat when it is reopened after a reload.
 * Null when there is nothing to write: the chat has no local entry yet (the transcript save creates it, total
 * included) or the entry already holds this total.
 */
export function withChatUsage(chats, conversationId, total) {
    const index = chats.findIndex((chat) => chat.id === conversationId && !chat.remote);
    if (index < 0 || sameSessionUsage(chats[index].usage, total))
        return null;
    return chats.map((chat, i) => (i === index ? { ...chat, usage: total } : chat));
}
/** "840", "1.2k", "48.2k", "482k", "1.2M": one decimal under 100 of a unit, none above. */
export function formatTokens(value) {
    if (!Number.isFinite(value) || value <= 0)
        return '0';
    const scaled = (n, suffix) => `${(n < 100 ? n.toFixed(1) : String(Math.round(n))).replace(/\.0$/, '')}${suffix}`;
    if (value < 1000)
        return String(Math.round(value));
    if (value < 999_500)
        return scaled(value / 1000, 'k');
    return scaled(value / 1_000_000, 'M');
}
/** "$0.41"; a positive amount under half a cent shows as "<$0.01". */
export function formatUsd(value) {
    if (!Number.isFinite(value) || value <= 0)
        return '$0.00';
    return value < 0.005 ? '<$0.01' : `$${value.toFixed(2)}`;
}
/**
 * The counter's tooltip: every figure the live event carries. With the conversation's size (a server from 2026-10-12)
 * it leads with that and says what the processed figure is; without it, it reads as before.
 */
export function describeLiveUsage(live) {
    const parts = live.contextTokens !== undefined
        ? [`Conversation ~${formatTokens(live.contextTokens)} tokens`, `Written this turn ${formatTokens(live.outputTokens)}`, `Processed this turn ${formatTokens(liveTotalTokens(live))} (each step re-reads the conversation)`, `Input ${formatTokens(live.inputTokens)}`]
        : [`Input ${formatTokens(live.inputTokens)}`, `Output ${formatTokens(live.outputTokens)}`];
    if (live.thinkingTokens > 0)
        parts.push(`Thinking ~${formatTokens(live.thinkingTokens)}`);
    if (live.cacheReadTokens > 0)
        parts.push(`Cache read ${formatTokens(live.cacheReadTokens)}`);
    if (live.cacheCreationTokens > 0)
        parts.push(`Cache write ${formatTokens(live.cacheCreationTokens)}`);
    if (live.costUsd !== undefined)
        parts.push(formatUsd(live.costUsd));
    if (live.round > 1)
        parts.push(`Round ${live.round}`);
    if (live.estimated)
        parts.push('estimate');
    return parts.join(' · ');
}
/**
 * What the processed total is made of, in words: every model call re-reads the conversation, and with caching most of
 * that re-reading is a cheap cache read. Said only when it is true of these figures.
 */
function processedNote(total) {
    return total.cacheReadTokens * 2 > totalTokens(total) ? 'most of it cached re-reads' : 'every step re-reads the conversation';
}
/** The session line's tooltip; with the chat's size, it says what the processed total is too. */
export function describeSessionUsage(total) {
    return [
        ...(total.contextTokens !== undefined ? [`This chat ~${formatTokens(total.contextTokens)} tokens`, `Processed this chat ${formatTokens(totalTokens(total))} tokens (${processedNote(total)})`] : []),
        `Input ${formatTokens(total.inputTokens)}`,
        `Output ${formatTokens(total.outputTokens)}`,
        `Cache read ${formatTokens(total.cacheReadTokens)}`,
        `Cache write ${formatTokens(total.cacheWriteTokens)}`,
        formatUsd(total.costUsd),
        `${total.turns} ${total.turns === 1 ? 'turn' : 'turns'}`,
    ].join(' · ');
}
/**
 * Per-conversation totals for the open widget: a chat's total grows with every finished turn, a new chat starts at
 * zero, switching chats shows that chat's own total, and a total read back from the local cache seeds a chat that
 * has not been touched since reload. Client-side only.
 */
export class ConversationUsage {
    totals = new Map();
    /** What each turn of this session added, by turn key: a turn counts once, and a recorded figure may replace a provisional one. */
    counted = new Map();
    total(conversationId) {
        return this.totals.get(conversationId) ?? EMPTY_SESSION_USAGE;
    }
    /** Add a finished turn (a turn that ended in a refusal or an error but still reported usage counts too). */
    record(conversationId, turn) {
        if (!turn)
            return this.total(conversationId);
        const next = addTurnUsage(this.total(conversationId), turn);
        this.totals.set(conversationId, next);
        return next;
    }
    /**
     * Count one turn toward its chat, exactly once. `recorded` marks the done result's usage (what the service charged);
     * anything else is provisional: the last live figures of a turn that was stopped, failed, or came from a server
     * without a recorded usage. A recorded figure replaces a provisional one counted earlier for the same turn (a failed
     * stream can report an error and then still deliver its done result); anything after a recorded figure is ignored.
     */
    countTurn(conversationId, turnKey, turn, recorded) {
        if (!turn)
            return this.total(conversationId);
        const prior = this.counted.get(turnKey);
        if (prior && (prior.recorded || !recorded))
            return this.total(conversationId);
        const base = prior ? withoutTurnUsage(this.total(conversationId), prior.usage) : this.total(conversationId);
        const added = addTurnUsage(base, turn);
        const next = prior ? { ...added, turns: base.turns } : added;
        this.totals.set(conversationId, next);
        this.counted.delete(turnKey);
        this.counted.set(turnKey, { conversationId, usage: turn, recorded });
        if (this.counted.size > COUNTED_TURNS_KEPT)
            this.counted.delete(this.counted.keys().next().value);
        return next;
    }
    /** A total restored from the local chat cache; never overrides what this session already counted. */
    seed(conversationId, saved) {
        const existing = this.totals.get(conversationId);
        if (existing)
            return existing;
        const parsed = parseSessionUsage(saved);
        if (parsed)
            this.totals.set(conversationId, parsed);
        return parsed ?? EMPTY_SESSION_USAGE;
    }
    /** The persisted shape for a chat, or undefined when nothing was spent in it. */
    saved(conversationId) {
        return this.totals.get(conversationId);
    }
    remove(conversationId) {
        this.totals.delete(conversationId);
        for (const [key, entry] of this.counted)
            if (entry.conversationId === conversationId)
                this.counted.delete(key);
    }
    clear() { this.totals.clear(); this.counted.clear(); }
}
//# sourceMappingURL=tokenUsage.js.map